import {useState, useEffect, useRef, HTMLInputTypeAttribute, ChangeEvent, KeyboardEvent, MutableRefObject} from 'react';
import EyeOpenIcon from '../../assets/images/eye-open.svg';
import EyeSlashIcon from '../../assets/images/eye-slash.svg';

interface Props {
  type?: HTMLInputTypeAttribute | undefined;
  id?: string | undefined;
  value?: string | undefined;
  name?: string | undefined;
  placeholder?: string | undefined;
  errors?: string[] | undefined;
  onInput?: (_: any) => void;
  readOnly?: boolean | undefined;
  prefix?: string | undefined;
  innerRef?: MutableRefObject<null>;
  maxLength?: number | undefined;
}

const padTimePart = (part: number) => part.toString().padStart(2, '0');

const parseTime = (value = '') => {
  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return {hour: 12, minute: 0};

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return {
    hour: hour >= 0 && hour <= 23 ? hour : 12,
    minute: minute >= 0 && minute <= 59 ? minute : 0
  };
};

const formatTime = (hour: number, minute: number) => `${padTimePart(hour)}:${padTimePart(minute)}`;

const isValidTime = (value: string) => {
  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return false;
  return Number(match[1]) <= 23 && Number(match[2]) <= 59;
};

interface TimeInputProps extends Props {
  inputValue: string;
  setInputValue: (_value: string) => void;
  hideErrors: boolean;
  setHideErrors: (_value: boolean) => void;
}

const TimeInput = ({
  id,
  name,
  placeholder,
  errors,
  onInput,
  readOnly,
  innerRef,
  maxLength,
  inputValue,
  setInputValue,
  hideErrors,
  setHideErrors
}: TimeInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const parsedTime = parseTime(inputValue);
  const period = parsedTime.hour >= 12 ? 'PM' : 'AM';
  const displayHour = parsedTime.hour % 12 || 12;
  const displayValue = inputValue && isValidTime(inputValue)
    ? `${padTimePart(displayHour)}:${padTimePart(parsedTime.minute)} ${period}`
    : inputValue;

  useEffect(() => {
    const closePicker = (event: MouseEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', closePicker);
    return () => document.removeEventListener('mousedown', closePicker);
  }, []);

  const updateTime = (hour: number, minute: number, selectedPeriod = period) => {
    const hour24 = selectedPeriod === 'PM' ? (hour % 12) + 12 : hour % 12;
    const nextValue = formatTime(hour24, minute);
    setInputValue(nextValue);
    onInput?.(nextValue);
    setHideErrors(true);
    setIsEditing(false);
  };

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setIsEditing(true);
    setInputValue(nextValue);
    onInput?.(nextValue);
    if (nextValue) setHideErrors(true);
  };

  const handleBlur = () => {
    if (!inputValue || !isValidTime(inputValue)) return;
    const {hour, minute} = parseTime(inputValue);
    const normalizedValue = formatTime(hour, minute);
    if (normalizedValue !== inputValue) {
      setInputValue(normalizedValue);
      onInput?.(normalizedValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter') {
      event.preventDefault();
      setIsOpen(true);
    }
    if (event.key === 'Escape') setIsOpen(false);
  };

  const clearTime = () => {
    setInputValue('');
    onInput?.('');
    setIsEditing(false);
    setIsOpen(false);
  };

  return (
    <div>
      <div className="c-input__box c-time-picker" ref={pickerRef}>
        <input
          className={`c-input ${inputValue ? 'label' : ''} ${errors ? 'c-input__error' : ''} ${readOnly ? 'c-input__disabled' : ''} c-input__time`}
          name={name}
          data-testid={id}
          value={isEditing ? inputValue : displayValue}
          type="text"
          readOnly={readOnly}
          onChange={handleInput}
          onFocus={() => {
            setIsEditing(false);
            setIsOpen(true);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          ref={innerRef}
          placeholder=""
          maxLength={maxLength}
          inputMode="numeric"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        />
        <button
          type="button"
          className="c-time-picker__toggle"
          aria-label="Open time picker"
          onMouseDown={event => event.preventDefault()}
          onClick={() => setIsOpen(open => !open)}
          disabled={readOnly}
        >
          <span aria-hidden="true">◷</span>
        </button>
        <span className="c-input__placeholder">{placeholder}</span>

        {isOpen && !readOnly && <div className="c-time-picker__popup" role="dialog" aria-label="Time picker">
          <div className="c-time-picker__columns">
            <div className="c-time-picker__column" aria-label="Hour">
              {Array.from({length: 12}, (_, index) => index + 1).map(hour => (
                <button
                  type="button"
                  className={hour === displayHour ? 'is-selected' : ''}
                  key={hour}
                  onClick={() => updateTime(hour, parsedTime.minute)}
                >{padTimePart(hour)}</button>
              ))}
            </div>
            <div className="c-time-picker__column" aria-label="Minute">
              {Array.from({length: 60}, (_, minute) => minute).map(minute => (
                <button
                  type="button"
                  className={minute === parsedTime.minute ? 'is-selected' : ''}
                  key={minute}
                  onClick={() => updateTime(displayHour, minute)}
                >{padTimePart(minute)}</button>
              ))}
            </div>
            <div className="c-time-picker__column c-time-picker__period" aria-label="Period">
              {['AM', 'PM'].map(option => (
                <button
                  type="button"
                  className={option === period ? 'is-selected' : ''}
                  key={option}
                  onClick={() => updateTime(displayHour, parsedTime.minute, option)}
                >{option}</button>
              ))}
            </div>
          </div>
          <div className="c-time-picker__actions">
            <button type="button" className="c-time-picker__clear" onClick={clearTime}>Clear</button>
          </div>
        </div>}
      </div>
      {!hideErrors && errors?.length && (
        <ul>
          {errors.map((err, index) => <li key={index} className="c-form__error">{err}</li>)}
        </ul>
      )}
    </div>
  );
};

const CFormInput = ({
  type = 'text',
  id,
  value,
  name,
  placeholder,
  errors,
  onInput,
  readOnly,
  prefix,
  innerRef,
  maxLength
}: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [hideErrors, setHideErrors] = useState(true);
  const [hidePassword, setHidePassword] = useState(true);
  const [inputType, setInputType] = useState<Props['type']>('');

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    setHidePassword(type === 'password');
    setInputType(type);
    // setting time type to text on load
    if (type === 'time') setInputType('text');
  }, [type]);

  useEffect(() => {
    setHideErrors(!errors);
  }, [errors]);

  if (type === 'time') {
    return <TimeInput
      {...{id, value, name, placeholder, errors, onInput, readOnly, innerRef, maxLength}}
      inputValue={inputValue}
      setInputValue={setInputValue}
      hideErrors={hideErrors}
      setHideErrors={setHideErrors}
    />;
  }

  const handleInput = async (e: ChangeEvent<HTMLInputElement>) => {
    e.persist();
    const { value } = e.target;
    setInputValue(value);
    onInput?.(value);
    if (value) {
      setHideErrors(true);
    }
  };

  const switchInputType = () => {
    setHidePassword(prev => !prev);
    setInputType(hidePassword ? 'text' : 'password');
  };

  const handleFocus = () => {
    if (type === 'time') setInputType('time');
  };

  const handleBlur = () => {
    if (type === 'time' && !inputValue) setInputType('text');
  };

  const allowedImgProps = { type: type };

  return (
    <div>
      <div className="c-input__box">
        <input
          className={`c-input ${inputValue ? 'label' : ''} ${
            errors ? 'c-input__error' : ''
          } ${prefix ? 'c-input__prefix--input' : ''}
          ${readOnly ? 'c-input__disabled' : ''}
          ${type === 'time' ? 'c-input__time' : ''}`}
          name={name}
          data-testid={id}
          value={inputValue}
          type={inputType}
          readOnly={readOnly}
          onChange={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          ref={innerRef}
          placeholder=""
          maxLength={maxLength}
        />
        {prefix && <div className="c-input__prefix">{prefix}</div>}
        <span className="c-input__placeholder">{placeholder}</span>

        {type === 'password' && <img
          {...allowedImgProps}
          className="c-input__eye"
          src={hidePassword ? EyeSlashIcon : EyeOpenIcon}
          alt={hidePassword ? 'password hidden' : 'password displayed'}
          onClick={switchInputType}
        />}
      </div>
      {!hideErrors && errors?.length && (
        <ul>
          {errors.map((err, index) => (
            <li key={index} className="c-form__error">
              {err}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CFormInput;
